import { assert, error, log } from '../app/logger.js';
import { settings } from '../app/settings.js';
import { UIQueueable } from '../ui/ui_queueable.js';
import {
  getImageMeta,
  //orientImage,
  toDataUrl,
} from '../util/image_processing.js';
import { window } from '../window.js';
import { GpxCollection } from './gpx_collection.js';
import { PicUploadViewer } from './picupload_viewer.js';

import '../media/media_processing_dialog.js';

/**
 * Used to places media such as pictures or gpx tracks on the map.
 */
class MapMediaLayer {
  constructor(map) {
    assert(map, `[map] no map given`);
    this.#map = map;
  }

  get picPopup() {
    return this.#picUploadViewer && this.#picUploadViewer.picUploadPopup;
  }

  get picMarkers() {
    return this.#picMarkers;
  }

  get picMarkerArray() {
    return Array.from(this.#picMarkers.values());
  }

  get gpxCollections() {
    return this.#gpxCollections;
  }

  get activeTracks() {
    return this.#gpxCollections.flatMap(collection => collection.activeTracks);
  }

  deactivateTracks() {
    let activeTracks = this.activeTracks;
    if (activeTracks.length == 0) {
      return false;
    }
    activeTracks.forEach(track => (track.active = false));
    return true;
  }

  get bounds() {
    return this.#getBounds(
      this.#gpxCollections,
      Array.from(this.#picMarkers.values())
    );
  }

  async add(files) {
    let pics = [];
    let lastGpxCollectionIndex = this.#gpxCollections.length;

    this.mediaProcessingDlg.show(files.length);

    // Load media files.
    for (let file of files) {
      if (file.name.toLowerCase().endsWith('.gpx')) {
        try {
          let gpxContent = await file.text();
          this.#gpxCollections.push(
            new GpxCollection({
              name: file.name,
              map: this.#map,
            }).setData(gpxContent)
          );
        } catch (e) {
          error(`[map:media] failed to parse GPX '${file.name}' file`, e);
        }
      } else if (file.type == 'image/jpeg') {
        let meta = await getImageMeta(file);
        pics.push([file, meta]);
      } else {
        log(
          `[map:media] file type '${file.type}' is not supported, '${file.name}' is ignored`
        );
      }
      this.mediaProcessingDlg.increment();
    }

    pics = pics.sort((a, b) => {
      if (a[1].date < b[1].date) {
        return -1;
      }
      if (a[1].date > b[1].date) {
        return 1;
      }
      return 0;
    });

    // Show tracks on the map.
    for (let i = lastGpxCollectionIndex; i < this.#gpxCollections.length; i++) {
      let gpxCollection = this.#gpxCollections[i];
      if (gpxCollection.tracksData.length == 0) {
        log(
          `[map:media] gpx file '${gpxCollection.name}' has no tracks, the file is ignored`
        );
        continue;
      }
      this.#gpxCollections[i].showTracks();
    }

    // Show pics on the map.
    let lastPicMarkerIndex = this.picMarkerArray.length;
    if (pics.length > 0) {
      lastPicMarkerIndex = this.#processPics(pics);
      if (this.#picUploadViewer) {
        this.#picUploadViewer.setPics(this.#allPics);
      }
    }

    if (settings.mediaProcessingDialogAutoHide) {
      this.mediaProcessingDlg.hide();
    }

    // Adjust map to fit bounds of uploaded media.
    let bounds = this.#getBounds(
      this.#gpxCollections.slice(lastGpxCollectionIndex),
      this.picMarkerArray.slice(lastPicMarkerIndex)
    );
    if (this.#map.constructor.isValidBounds(bounds)) {
      this.#map.fitBounds(bounds);
    } else {
      // If bounds are squashed (a single pic marker is created),
      // then adjust map to show the pic marker.
      let { north: lat, east: lng } = bounds;
      if (this.#map.constructor.isValidCoords({ lat, lng })) {
        this.#map.panTo({ lat, lng });
        this.#map.zoom = 15;
      }
    }
  }

  get picUploadViewer() {
    if (!this.#picUploadViewer) {
      this.#picUploadViewer = new PicUploadViewer({
        map: this.#map,
        markers: this.#picMarkers,
        pics: this.#allPics,
      });
    }
    return this.#picUploadViewer;
  }

  get mediaProcessingDlg() {
    if (!this.#mediaProcessingDlg) {
      this.#mediaProcessingDlg = document.body.appendChild(
        document.createElement('media-processing-dialog')
      );
    }
    return this.#mediaProcessingDlg;
  }

  showPics(timeOffset = 0) {
    log(`[map:media] show pictures for ${timeOffset} timeOffset`);
    for (let marker of this.#picMarkers.values()) {
      marker.deattach();
    }
    this.#picMarkers.clear();

    let lastMarker = null;
    for (let i = 0; i < this.#allPics.length; i++) {
      let data = this.#allPics[i];
      let { meta } = data;
      let { lat, lng } = meta;
      let position = { lat, lng };

      // If no coordinates, then associated a pic with the last marker, or put it
      // into the center of the map, if there's none.
      if (!lat || !lng || meta.origNoGPS) {
        if (meta.date) {
          if (timeOffset != 0) {
            meta.origDate = meta.date;
            meta.date = new Date(meta.date.valueOf() + timeOffset * 1000);
          }
          meta.trackPoint = this.#findPositionFromTracks(meta.date);
          if (meta.trackPoint) {
            meta.lat = meta.trackPoint.lat;
            meta.lng = meta.trackPoint.lng;
            meta.origNoGPS = true;
          }
        }
        position =
          meta.trackPoint ||
          (lastMarker && lastMarker.position) ||
          this.#map.center;
        lat = position.lat;
        lng = position.lng;
      }

      lastMarker = this.#getMarkerFor({ lat, lng });
      data.marker = lastMarker;

      if (!lastMarker.anchorPicIndex) {
        lastMarker.anchorPicIndex = i;
      }
      if (++lastMarker.picCount > 1) {
        lastMarker.title = `${lastMarker.picCount} photos`;
      }
      lastMarker.picCount++;

      log(
        `[map:media] '${meta.name}' picture is added to ${lastMarker.id} marker at (${lat}, ${lng})`
      );
    }
  }

  #processPics(pics) {
    let lastPicMarkerIndex = this.picMarkerArray.length;
    for (let [file, meta] of pics) {
      let data = this.#getPicData(file, meta);
      this.#allPics.push(data);
    }
    this.showPics();
    return lastPicMarkerIndex;
  }

  #getBounds(gpxCollections, picMarkers) {
    let points = gpxCollections.flatMap(l =>
      l.tracksData.flatMap(t => t.points)
    );
    points.push(...picMarkers.map(m => m.position));
    return this.#map.constructor.boundsFor(points);
  }

  #getMarkerFor({ lat, lng }) {
    let markerid =
      String(parseInt((lat || 0) * Math.pow(10, 6))) +
      String(parseInt((lng || 0) * Math.pow(10, 6)));

    let marker = this.#picMarkers.get(markerid);
    if (marker) {
      return marker;
    }

    marker = new window.Marker({
      map: this.#map,
      position: { lat, lng },
      icon: null, // standar marker for performance
      title: 'Photo',
      label: 'P',
      visible: true,
      clickable: true,
    });
    marker.id = markerid;
    marker.picCount = 0;
    marker.on(
      'click',
      UIQueueable(
        function (marker) {
          this.picUploadViewer.show(marker);
        }.bind(this, marker)
      )
    );
    log(`[map:media] ${markerid} marker is set at (${lat}, ${lng})`);
    this.#picMarkers.set(markerid, marker);

    return marker;
  }

  #getPicData(file, meta) {
    // Temporary disable image orientation, because newer cameras
    // make a picture aligned correctly with non 1 orientation in EXIF.
    // In order to support all campera types, orientation feature
    // should be rather supported via UI.
    // let blobPromise = orientImage(file, meta.orientation);
    let blobPromise = Promise.resolve(file);
    let urlPromise = blobPromise.then(blob => toDataUrl(blob, 'image/jpeg'));
    return {
      blobPromise,
      urlPromise,
      meta,
    };
  }

  #findPositionFromTracks(date) {
    let points = this.#gpxCollections.map(c => c.findPoint(date));
    return GpxCollection.findPointIn(points, date);
  }

  #allPics = [];
  #gpxCollections = [];
  #map;
  #mediaProcessingDlg;
  #picMarkers = new Map();
  #picUploadViewer;
}

export { MapMediaLayer };
